import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../api/client';
import { decode, decodeBlob, getContext } from '../audio/context';
import { Recorder, isRecordingSupported, micErrorMessage, requestWakeLock } from '../audio/recorder';
import {
  TimelineEngine,
  mixdown,
  mixdownToWav,
  type EngineTrack,
} from '../audio/timeline';
import Timeline, { RULER_H, LANE_H } from './Timeline';
import ClipTools from './ClipTools';
import ExportPanel from './ExportPanel';
import TrackPicker from './TrackPicker';
import AccountModal from './AccountModal';
import LibraryModal from './LibraryModal';
import { useAccount, accountsEnabled } from '../lib/supabase';
import { saveProject, type ProjectSnapshot } from '../lib/library';
import { useStudio, TRACK_COLORS, type Clip } from '../state/studio';
import { track } from '../lib/track';
import { fmtTime, uid } from '../format';

function floatToBuffer(f32: Float32Array, sr: number): AudioBuffer {
  const b = getContext().createBuffer(1, Math.max(1, f32.length), sr);
  b.getChannelData(0).set(f32);
  return b;
}

export default function Studio({ onExit }: { onExit: () => void }) {
  const [s, dispatch] = useStudio();
  const engine = useMemo(() => new TimelineEngine(), []);
  const recorder = useRef<Recorder | null>(null);
  const wakeLock = useRef<{ release(): Promise<void> } | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bpmEdit, setBpmEdit] = useState<string | null>(null);
  const [tools, setTools] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [picker, setPicker] = useState<'rec' | 'import' | null>(null);
  const [editTrack, setEditTrack] = useState<{ id: string; value: string } | null>(null);
  const recStart = useRef(0);

  /* ------------------------------------------------------------- comptes */
  const user = useAccount();
  const [accountOpen, setAccountOpen] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const [cloudBusy, setCloudBusy] = useState(false);
  const cloudProject = useRef<{ id: string; name: string } | null>(null);

  const saveCloud = async () => {
    if (!user) { setAccountOpen(true); return; }
    if (!s.tracks.some((t) => t.clips.length)) { setError('Rien à sauvegarder pour l’instant.'); return; }
    const name = window.prompt('Nom du projet :', cloudProject.current?.name ?? 'Mon projet');
    if (!name?.trim()) return;
    setCloudBusy(true);
    setBusy('Sauvegarde du projet…');
    try {
      const id = await saveProject(
        user.id, name.trim(),
        { bpm: s.bpm, beatsPerBar: s.beatsPerBar, tracks: s.tracks },
        cloudProject.current?.id ?? null,
      );
      cloudProject.current = { id, name: name.trim() };
      setBusy('Projet sauvegardé ✓');
      window.setTimeout(() => setBusy(null), 1400);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sauvegarde impossible.');
      setBusy(null);
    } finally {
      setCloudBusy(false);
    }
  };
  const posRef = useRef(0);       // position 60 Hz pour le canvas (sans re-render)
  const lastSetPos = useRef(-1);  // throttle du state React (~10 Hz)

  const engineTracks = useCallback(
    (): EngineTrack[] =>
      s.tracks.map((t) => ({
        volume: t.volume,
        muted: t.muted,
        solo: t.solo,
        clips: t.clips.map((c) => ({ buffer: c.buffer, start: c.start, offset: c.offset, duration: c.duration })),
      })),
    [s.tracks],
  );

  useEffect(() => track('studio-ouvert'), []);

  useEffect(() => {
    engine.onChange = () => setPlaying(engine.isPlaying);
    engine.onEnded = () => {
      setPlaying(false);
      const t = engine.time; // stop() conserve la position de fin
      dispatch({ type: 'playhead', pos: t });
      setPos(t);
    };
    let raf = 0;
    const tick = () => {
      if (engine.isPlaying) {
        const t = engine.time;
        posRef.current = t; // le canvas lit ce ref à 60 Hz
        // le state React (affichage du temps, etc.) ne bouge qu'à ~10 Hz
        if (Math.abs(t - lastSetPos.current) > 0.1) {
          lastSetPos.current = t;
          setPos(t);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      engine.dispose();
      // micro + wake lock : ne jamais les laisser ouverts en quittant le studio
      recorder.current?.dispose();
      recorder.current = null;
      wakeLock.current?.release().catch(() => undefined);
      wakeLock.current = null;
    };
  }, [engine, dispatch]);

  const selectedTrackId = s.selectedTrack ?? s.tracks[0]?.id;

  const play = useCallback(() => {
    engine.play(engineTracks(), s.playhead).catch((e) => setError(String(e)));
  }, [engine, engineTracks, s.playhead]);

  const stop = useCallback(() => {
    const t = engine.time;
    engine.stop();
    dispatch({ type: 'playhead', pos: t });
    setPos(t);
  }, [engine, dispatch]);

  /* ---------------------------------------------------- enregistrer dans une piste */
  const startRec = useCallback(async () => {
    const rec = new Recorder();
    try {
      await rec.start();
    } catch (e) {
      setError(micErrorMessage(e));
      rec.dispose();
      return;
    }
    // si la timeline joue, la prise se calera là où on a VRAIMENT commencé
    // (posRef est toujours frais, même si la tête a bougé pendant la
    // demande d'autorisation micro)
    recStart.current = engine.isPlaying ? engine.time : posRef.current;
    recorder.current = rec;
    wakeLock.current = await requestWakeLock();
    setRecording(true);
  }, [engine]);

  const stopRec = useCallback(async () => {
    const rec = recorder.current;
    if (!rec) return;
    setBusy('Préparation de la prise…');
    try {
      const blob = await rec.stop();
      recorder.current = null;
      wakeLock.current?.release().catch(() => undefined);
      wakeLock.current = null;
      const buffer = await decodeBlob(blob);
      track('enregistrement-termine', { contexte: 'studio', duree_sec: Math.round(buffer.duration) });
      const trackId = selectedTrackId;
      if (trackId) {
        dispatch({
          type: 'clip/add',
          trackId,
          clip: { name: 'prise', buffer, start: Math.max(0, recStart.current), offset: 0, duration: buffer.duration },
          select: true,
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    } finally {
      setRecording(false);
      setBusy(null);
    }
  }, [dispatch, selectedTrackId]);

  /* ---------------------------------------------------- importer un fichier dans une piste */
  const onFile = useCallback(
    async (file: File | undefined) => {
      if (!file) return;
      setBusy('Lecture du fichier…');
      try {
        const buffer = await decodeBlob(file);
        track('chargement-fichier', { format: file.name.split('.').pop()?.toLowerCase() ?? '?', contexte: 'studio' });
        const trackId = selectedTrackId;
        if (trackId) {
          dispatch({
            type: 'clip/add',
            trackId,
            clip: { name: file.name.replace(/\.[^.]+$/, ''), buffer, start: Math.max(0, s.playhead), offset: 0, duration: buffer.duration },
            select: true,
          });
        }
      } catch {
        setError('Fichier non décodable par ce navigateur (essaie un WAV ou MP3).');
      } finally {
        setBusy(null);
      }
    },
    [dispatch, s.playhead, selectedTrackId],
  );

  /* ---------------------------------------------------- export / fusion / débruitage piste */
  const sr = () => getContext().sampleRate;

  const anyAudible = useCallback(() => {
    const anySolo = s.tracks.some((t) => t.solo);
    return s.tracks.some((t) => t.clips.length && !t.muted && (!anySolo || t.solo));
  }, [s.tracks]);

  const mergeAll = useCallback(() => {
    if (!s.tracks.some((t) => t.clips.length)) return;
    if (!anyAudible()) { setError('Aucune piste audible à fusionner (tout est muté ?).'); return; }
    const f = mixdown(engineTracks(), sr());
    dispatch({ type: 'merge', buffer: floatToBuffer(f, sr()) });
  }, [engineTracks, s.tracks, dispatch, anyAudible]);

  const denoiseTrack = useCallback(
    async (trackId: string) => {
      const t = s.tracks.find((x) => x.id === trackId);
      if (!t || !t.clips.length) return;
      setBusy('Nettoyage de la piste…');
      try {
        const solo: EngineTrack = {
          volume: 1, muted: false, solo: false,
          clips: t.clips.map((c) => ({ buffer: c.buffer, start: c.start, offset: c.offset, duration: c.duration })),
        };
        const wav = mixdownToWav([solo], sr());
        const { wav: out } = await api.denoise(wav, { amount: 0.6, useHighpass: true, useHum: true });
        const buffer = await decode(out);
        dispatch({ type: 'track/bounce', id: trackId, buffer, name: 'nettoyé' });
      } catch (e) {
        setError(e instanceof api.ApiError ? e.message : 'Le serveur de nettoyage ne répond pas.');
      } finally {
        setBusy(null);
      }
    },
    [s.tracks, dispatch],
  );

  /* ---------------------------------------------------- raccourci espace */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) return;
      if (e.code === 'Space') { e.preventDefault(); if (!recording) { playing ? stop() : play(); } }
      else if (e.key === 'Delete' && s.selectedClip) dispatch({ type: 'clip/remove', id: s.selectedClip });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [playing, play, stop, s.selectedClip, dispatch, recording]);

  /* --------------------------------------- choix de la piste de destination */
  const askOrGo = (mode: 'rec' | 'import') => {
    if (s.tracks.length > 1) { setPicker(mode); return; }
    if (mode === 'rec') void startRec();
    else fileInput.current?.click();
  };

  const onPickTrack = (target: string | 'new') => {
    const mode = picker;
    setPicker(null);
    let id = target;
    if (target === 'new') {
      id = uid();
      dispatch({ type: 'track/add', id });
    }
    dispatch({ type: 'track/select', id });
    if (mode === 'rec') void startRec();
    else if (mode === 'import') fileInput.current?.click();
  };

  const headPos = playing ? pos : s.playhead;
  if (!playing) posRef.current = headPos; // à l'arrêt, le canvas suit la tête posée

  // clip sélectionné (objet complet) + piste porteuse
  let selClip: Clip | null = null;
  let selClipTrack: string | null = null;
  for (const t of s.tracks) {
    const c = t.clips.find((x) => x.id === s.selectedClip);
    if (c) { selClip = c; selClipTrack = t.id; break; }
  }
  const totalClips = s.tracks.reduce((n, t) => n + t.clips.length, 0);

  return (
    <div className="studio">
      <div className="studio-top">
        <div className="brand">Quan<em>time</em> · <span style={{ color: 'var(--violet)' }}>Studio</span></div>
        <div className="vrule" />
        <button className="btn icon" onClick={() => { stop(); dispatch({ type: 'playhead', pos: 0 }); setPos(0); }} aria-label="Retour au début" title="Revenir au début">⏮</button>
        <button className="btn play" onClick={playing ? stop : play} disabled={recording} title="Lire / arrêter la timeline (Espace)">{playing ? '■ Stop' : '▶ Lecture'}</button>
        <button
          className={`btn ${recording ? 'primary' : 'rec'}`}
          onClick={recording ? stopRec : () => askOrGo('rec')}
          disabled={!isRecordingSupported() && !recording}
          title="Enregistrer au micro dans une piste, à la position de la tête de lecture"
        >
          {recording ? '■ Arrêter' : '● Enregistrer'}
        </button>
        <button className="btn" onClick={() => askOrGo('import')} disabled={recording} title="Importer un fichier audio dans une piste">Importer…</button>
        <span className="mono" style={{ fontSize: 13, color: 'var(--muted)' }}>{fmtTime(headPos)}</span>
        <div className="vrule" />
        <label className="mono" style={{ fontSize: 12, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            className="field num" type="number" min={30} max={300}
            value={bpmEdit ?? String(Math.round(s.bpm))}
            onChange={(e) => setBpmEdit(e.target.value)}
            onBlur={() => {
              const n = Number(bpmEdit);
              if (bpmEdit != null && Number.isFinite(n) && n > 0) dispatch({ type: 'bpm', bpm: n });
              setBpmEdit(null);
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            aria-label="BPM"
          /> bpm
        </label>
        <span className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{s.beatsPerBar}/4</span>
        <div className="grow" />
        {accountsEnabled() && (
          <button
            className="btn sm"
            onClick={() => void saveCloud()}
            disabled={cloudBusy || recording}
            title={user ? 'Sauvegarder le projet dans ma bibliothèque (cloud)' : 'Se connecter pour sauvegarder le projet'}
          >{cloudBusy ? '☁ …' : '☁ Sauvegarder'}</button>
        )}
        {accountsEnabled() && (
          <button
            className={`btn sm${user ? '' : ' ghost'}`}
            onClick={() => setAccountOpen(true)}
            title={user ? `Compte : ${user.email} · bibliothèque` : 'Se connecter / créer un compte'}
          >👤</button>
        )}
        <button className="btn sm" onClick={mergeAll} title="Fusionner toutes les pistes audibles en une seule">Fusionner</button>
        <button
          className="btn green sm"
          onClick={() => {
            if (!s.tracks.some((t) => t.clips.length)) { setError('Aucun clip à exporter.'); return; }
            setExportOpen(true);
          }}
          title="Choisir les pistes et le format (WAV / MP3), puis télécharger"
        >⬇ Exporter…</button>
        <button
          className="btn ghost sm"
          disabled={recording}
          onClick={() => {
            if (s.tracks.some((t) => t.clips.length) &&
                !window.confirm('Quitter le studio ? Le projet multipiste actuel sera perdu.')) return;
            stop();
            onExit();
          }}
        >Prise unique ▸</button>
      </div>

      <div className="studio-body">
        <div className="mixer">
          <div className="mixer-ruler" style={{ height: RULER_H }} />
          {s.tracks.map((t, i) => {
            const on = t.id === selectedTrackId;
            return (
              <div
                key={t.id}
                className={`mixer-row${on ? ' on' : ''}`}
                style={{ height: LANE_H }}
                onClick={() => dispatch({ type: 'track/select', id: t.id })}
              >
                <div className="mx-top">
                  <span className="mx-dot" style={{ background: TRACK_COLORS[i % TRACK_COLORS.length] }} />
                  {editTrack?.id === t.id ? (
                    <input
                      className="mx-rename"
                      value={editTrack.value}
                      autoFocus
                      onChange={(e) => setEditTrack({ id: t.id, value: e.target.value })}
                      onClick={(e) => e.stopPropagation()}
                      onBlur={() => {
                        const name = editTrack.value.trim();
                        if (name) dispatch({ type: 'track/patch', id: t.id, patch: { name } });
                        setEditTrack(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        if (e.key === 'Escape') setEditTrack(null);
                      }}
                      aria-label="Nom de la piste"
                    />
                  ) : (
                    <span
                      className="mx-name"
                      title="Double-clique (ou ✎) pour renommer"
                      onDoubleClick={(e) => { e.stopPropagation(); setEditTrack({ id: t.id, value: t.name }); }}
                    >{t.name}</span>
                  )}
                  <button
                    className="mx-icon pen" title="Renommer la piste"
                    onClick={(e) => { e.stopPropagation(); setEditTrack({ id: t.id, value: t.name }); }}
                  >✎</button>
                  <button
                    className={`mx-btn${t.muted ? ' act' : ''}`}
                    title="Muet : coupe cette piste à la lecture et à l'export"
                    onClick={(e) => { e.stopPropagation(); dispatch({ type: 'track/patch', id: t.id, patch: { muted: !t.muted } }); }}
                  >M</button>
                  <button
                    className={`mx-btn s${t.solo ? ' act' : ''}`}
                    title="Solo : n'entendre que cette piste (et les autres solos)"
                    onClick={(e) => { e.stopPropagation(); dispatch({ type: 'track/patch', id: t.id, patch: { solo: !t.solo } }); }}
                  >S</button>
                </div>
                <div className="mx-bot">
                  <input
                    type="range" min={0} max={100} value={Math.round(t.volume * 100)}
                    onChange={(e) => dispatch({ type: 'track/patch', id: t.id, patch: { volume: Number(e.target.value) / 100 } })}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Volume ${t.name}`}
                    title={`Volume de la piste (${Math.round(t.volume * 100)} %)`}
                  />
                  <button className="mx-icon" title="Nettoyer la piste : enlever souffle et ronflette (serveur)" onClick={(e) => { e.stopPropagation(); denoiseTrack(t.id); }}>🧹</button>
                  <button className="mx-icon" title="Supprimer la piste et ses clips" onClick={(e) => { e.stopPropagation(); dispatch({ type: 'track/remove', id: t.id }); }}>🗑</button>
                </div>
              </div>
            );
          })}
          <button className="btn dashed block" style={{ marginTop: 8 }} onClick={() => dispatch({ type: 'track/add' })}>＋ Ajouter une piste</button>
        </div>

        <div className="timeline-col">
          <Timeline s={s} dispatch={dispatch} playhead={headPos} livePos={posRef} onSeek={(t) => { engine.seek(t, engineTracks()); posRef.current = t; setPos(t); }} />
          {totalClips === 0 && !recording && (
            <div className="tl-empty">
              <p>Ta prise devient la première piste.</p>
              <div className="tl-empty-btns">
                <button className="btn rec big" onClick={startRec} disabled={!isRecordingSupported()}>● Enregistrer</button>
                <button className="btn big" onClick={() => fileInput.current?.click()}>Importer un audio…</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {selClip && (
        <div className="studio-clipbar">
          <button className="btn primary" onClick={() => setTools(true)}>✨ Traiter</button>
          <button
            className="btn"
            onClick={() => dispatch({ type: 'clip/split', id: selClip!.id, at: headPos })}
            title="Couper le clip au niveau de la tête de lecture"
          >✂ Couper</button>
          <button
            className="btn"
            onClick={() => {
              if (!selClipTrack) return;
              dispatch({
                type: 'clip/add', trackId: selClipTrack,
                clip: {
                  name: selClip!.name, buffer: selClip!.buffer,
                  start: selClip!.start + selClip!.duration,
                  offset: selClip!.offset, duration: selClip!.duration,
                },
                select: true,
              });
            }}
            title="Dupliquer le clip juste après (pour boucler)"
          >⧉ Dupliquer</button>
          <button className="btn ghost" onClick={() => { setTools(false); dispatch({ type: 'clip/remove', id: selClip!.id }); }}>✕ Supprimer</button>
          <span className="note">Glisse pour déplacer · tire les bords pour rogner.</span>
        </div>
      )}

      {tools && selClip && (
        <ClipTools
          clip={selClip}
          projectBpm={s.bpm}
          dispatch={dispatch}
          onClose={() => setTools(false)}
          onError={setError}
        />
      )}

      {exportOpen && (
        <ExportPanel
          tracks={s.tracks}
          bpm={s.bpm}
          onClose={() => setExportOpen(false)}
          onError={setError}
        />
      )}

      {picker && (
        <TrackPicker
          tracks={s.tracks}
          title={picker === 'rec' ? 'Enregistrer dans quelle piste ?' : 'Importer dans quelle piste ?'}
          onPick={onPickTrack}
          onCancel={() => setPicker(null)}
        />
      )}

      {accountOpen && (
        <AccountModal
          user={user}
          onClose={() => setAccountOpen(false)}
          onOpenLibrary={() => { setAccountOpen(false); setLibOpen(true); }}
        />
      )}
      {libOpen && user && (
        <LibraryModal
          user={user}
          context="studio"
          onClose={() => setLibOpen(false)}
          onLoadTake={(buffer, row) => {
            const trackId = selectedTrackId;
            if (!trackId) return;
            dispatch({
              type: 'clip/add', trackId,
              clip: { name: row.name, buffer, start: Math.max(0, s.playhead), offset: 0, duration: buffer.duration },
              select: true,
            });
          }}
          onLoadProject={(snap: ProjectSnapshot, row) => {
            if (s.tracks.some((t) => t.clips.length) &&
                !window.confirm('Remplacer l’arrangement actuel par ce projet ?')) return;
            stop();
            cloudProject.current = { id: row.id, name: row.name };
            dispatch({ type: 'bpm', bpm: snap.bpm });
            dispatch({ type: 'beatsPerBar', n: snap.beatsPerBar });
            dispatch({ type: 'tracks/set', tracks: snap.tracks });
            dispatch({ type: 'track/select', id: snap.tracks[0]?.id ?? null });
            dispatch({ type: 'playhead', pos: 0 });
            setPos(0);
          }}
        />
      )}

      {busy && <div className="studio-toast">{busy}</div>}
      {error && (
        <div className="toast" role="alert">
          <span>{error}</span>
          <button className="btn xs ghost" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <input
        ref={fileInput} type="file" className="sr"
        accept="audio/*,.wav,.mp3,.m4a,.aac,.flac,.ogg,.opus,.webm"
        onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = ''; }}
      />
    </div>
  );
}
